import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  cancelBookingRequest,
  createBookingRequest,
  getBookingsRequest,
  sendBookingInvoiceRequest,
  updateBookingRequest,
} from "../../api/bookingApi";
import { CreateBookingRequest } from "../../models/RequestModels/CreateBookingRequest";
import { UpdateBookingRequest } from "../../models/RequestModels/UpdateBookingRequest";
import { BookingResponse } from "../../models/ResponseModels/BookingResponse";
import {
  getApiErrorMessage,
  getApiErrorPayload,
} from "../../utils/helperFunctions/apiErrorHelper";

interface FetchBookingsParams {
  from?: string;
  until?: string;
  unit_id?: number;
  status?: string;
}

interface UpdateBookingPayload {
  id: number;
  body: UpdateBookingRequest;
}

interface BookingState {
  bookings: BookingResponse[];
  loading: boolean;
  error: string | null;
}

const initialState: BookingState = {
  bookings: [],
  loading: false,
  error: null,
};

export const fetchBookings = createAsyncThunk(
  "bookings/fetchBookings",
  async (params: FetchBookingsParams | undefined, { rejectWithValue }) => {
    try {
      const response = await getBookingsRequest(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const createBooking = createAsyncThunk(
  "bookings/createBooking",
  async (body: CreateBookingRequest, { rejectWithValue }) => {
    try {
      const response = await createBookingRequest(body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const updateBooking = createAsyncThunk(
  "bookings/updateBooking",
  async ({ id, body }: UpdateBookingPayload, { rejectWithValue }) => {
    try {
      const response = await updateBookingRequest(id, body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const cancelBooking = createAsyncThunk(
  "bookings/cancelBooking",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await cancelBookingRequest(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const sendBookingInvoice = createAsyncThunk(
  "bookings/sendBookingInvoice",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await sendBookingInvoiceRequest(id);
      return response.message;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

const replaceBooking = (
  bookings: BookingResponse[],
  booking: BookingResponse,
): BookingResponse[] => {
  return bookings.map((currentBooking) =>
    currentBooking.id === booking.id ? booking : currentBooking,
  );
};

const bookingSlice = createSlice({
  name: "bookings",
  initialState,
  reducers: {
    clearBookingError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = action.payload;
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to load bookings.",
        );
      })
      .addCase(createBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBooking.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings.unshift(action.payload);
      })
      .addCase(createBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to create booking.",
        );
      })
      .addCase(updateBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBooking.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = replaceBooking(state.bookings, action.payload);
      })
      .addCase(updateBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to update booking.",
        );
      })
      .addCase(cancelBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelBooking.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = replaceBooking(state.bookings, action.payload);
      })
      .addCase(cancelBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to cancel booking.",
        );
      })
      .addCase(sendBookingInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendBookingInvoice.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendBookingInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to send invoice.",
        );
      });
  },
});

export const { clearBookingError } = bookingSlice.actions;

export default bookingSlice.reducer;
